import { useEffect, useState } from "react";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// What I've earned beyond the clock this week, day by day: piece pay for the
// piece-rate jobs I finished, and commission on what I sold (as the company
// figures it). Payroll adds the same numbers to hours.

interface Ev {
  at: number;
  cents: number;
  label: string;
}
const DAY = 86_400_000;
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
function weekStart(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime() - ((d.getDay() + 6) % 7) * DAY;
}
const dayOf = (ms: number) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export function MyEarnings({ tenantId }: { tenantId: string }) {
  const [from] = useState(() => weekStart(Date.now()));
  const [data, setData] = useState<{ piece: Ev[]; commission: Ev[]; commissionRateBps: number } | null>(null);
  const [day, setDay] = useState<number | null>(null);

  useEffect(() => {
    void call<{ piece: Ev[]; commission: Ev[]; commissionRateBps: number }>("getMyEarnings", { tenantId, from, to: from + 7 * DAY }, { mutation: false })
      .then(setData)
      .catch(() => setData({ piece: [], commission: [], commissionRateBps: 0 }));
  }, [tenantId, from]);

  if (!data) return null;
  if (!data.piece.length && !data.commission.length && !data.commissionRateBps) return null;
  const days = Array.from({ length: 7 }, (_, i) => from + i * DAY);
  const sum = (evs: Ev[], d?: number) => evs.filter((e) => d === undefined || dayOf(e.at) === d).reduce((s, e) => s + e.cents, 0);
  const today = dayOf(Date.now());
  const shown = day ?? today;
  const events = [...data.piece.map((e) => ({ ...e, kind: "piece" as const })), ...data.commission.map((e) => ({ ...e, kind: "commission" as const }))].filter((e) => dayOf(e.at) === shown).sort((a, b) => a.at - b.at);

  return (
    <Card className="space-y-2">
      <div className="flex items-baseline justify-between">
        <CardTitle className="mb-0">My earnings this week</CardTitle>
        <span className="text-sm font-bold text-ink">{money(sum(data.piece) + sum(data.commission))}</span>
      </div>
      <p className="text-xs text-steel">
        Today: <strong className="text-ink">{money(sum(data.piece, today))}</strong> piece pay
        {data.commission.length || data.commissionRateBps ? (
          <>
            {" · "}
            <strong className="text-ink">{money(sum(data.commission, today))}</strong> commission
          </>
        ) : null}
        {" "}(hours are paid from the Time Clock on top)
      </p>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const total = sum(data.piece, d) + sum(data.commission, d);
          return (
            <button
              key={d}
              className={cn("rounded-lg border px-1 py-1.5 text-center", shown === d ? "border-brandorange bg-brandorange/10" : "border-steel/20 hover:border-steel/50", d > today && "opacity-50")}
              onClick={() => setDay(d)}
            >
              <div className="text-[10px] uppercase text-steel">{new Date(d).toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className={cn("text-xs font-semibold", total ? "text-ink" : "text-steel/60")}>{total ? money(total).replace(/\.00$/, "") : "—"}</div>
            </button>
          );
        })}
      </div>
      {events.length ? (
        <div className="divide-y divide-steel/10 text-xs">
          {events.map((e, i) => (
            <div key={i} className="flex justify-between gap-2 py-1">
              <span className="min-w-0 truncate text-ink">
                <span className="text-steel">{e.kind === "piece" ? "Piece · " : "Commission · "}</span>
                {e.label}
              </span>
              <span className="shrink-0 text-ink">{money(e.cents)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-steel">Nothing on {shown === today ? "today" : new Date(shown).toLocaleDateString(undefined, { weekday: "long" })} yet.</p>
      )}
      {data.commissionRateBps ? <p className="text-[11px] text-steel">Your commission rate: {data.commissionRateBps / 100}%.</p> : null}
    </Card>
  );
}
